import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useState } from "react";
import Loader from "../../../components/elements/Loader";
import SnackbarAlert from "../../../components/elements/SnackbarAlert";
import { useFormData } from "../../../helper/FormDataProvider";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { ClearanceStatus, TRIP_STATUS } from "../../../utils/constants";
import {
  customDateFormat,
  getFormattedLocalTime,
  getFormattedLocalTimeWithMinusHour
} from "../../../utils/dateUtils";
import ROUTE_PATHS from "../../../utils/routePaths";
import { airportCodeOnApprove } from "../../../utils/Utils";
import ActionButtons from "../../Trip Details/views/ActionButtons";
import AfterHoursCard from "../../Trip Details/views/AfterHoursCard";
import AircraftDetails from "../../Trip Details/views/AircraftDetails";
import AirportLocationInformation from "../../Trip Details/views/AirportLocationInformation";
import PassengerInformation from "../../Trip Details/views/PassengersInfo";
import PaymentInformation from "../../Trip Details/views/PaymentInformation";
import SpecialOnboardScreeningCard from "../../Trip Details/views/SpecialOnboardScreeningCard";
import TripID from "../../Trip Details/views/TripID";
import "../css/HistoricTripsDetails.css";
import ApprovalStatus from "./ApprovedStatus";
import HistoricTripsDetailsHeader from "./HistoricTripsDetailsHeader";
import PersonalDetails from "../../Trip Details/views/PersonalDetails";

const HistoricTripsDetails = () => {
  const { getTripById } = useLandingAuthApi();

  const { tripId } = useParams();
  const [resolvedMtow, setResolvedMtow] = useState();
  const location = useLocation();
  const { userData } = useFormData();

  const navigate = useNavigate();
  const {
    data: historicTripDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["historicTripDetails", tripId],
    queryFn: () => getTripById(tripId),
    refetchOnMount: "always",
  });

  const tripData = historicTripDetails?.data;
  const isExempted = tripData?.isExempted;
  const currentPage = location?.state?.currentPage;
  const handleEscortClick = (trips) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("fromScreen", "historicTrips");
    navigate(ROUTE_PATHS.ESCORT_DETAILS(trips?.id), {
      state: {
        currentPage,
        fromScreen: "historicTrips",
      },
    });
  };
  const handleAfterHoursClick = (trips) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("fromScreen", "historicTrips");
    navigate(ROUTE_PATHS.AFTER_HOURS_DETAILS(trips?.id), {
      state: {
        currentPage,
        fromScreen: "historicTrips",
      },
    });
  };
  const checkDepartureOvertime = () => {
    if (tripData?.requiresDepartureOvertime) {
      return {
        departureAirportOpeningTime: getFormattedLocalTimeWithMinusHour(
          tripData?.departureTime
        ),
        scheduledTime: getFormattedLocalTime(tripData.departureTime),
      };
    }
  };
  const checkArrivalOvertime = () => {
    if (tripData?.requiresArrivalOvertime) {
      return {
        requiresArrivalClosingOvertime: getFormattedLocalTimeWithMinusHour(
          tripData.arrivalTime
        ),
        scheduledTime: getFormattedLocalTime(tripData?.arrivalTime),
      };
    }
  };
  const timingDepartureInfo = tripData?.requiresDepartureOvertime
    ? checkDepartureOvertime()
    : null;
  const timingArrivalInfo = tripData?.requiresArrivalOvertime
    ? checkArrivalOvertime()
    : null;

  const isValidStatus = (status) =>
    status !== ClearanceStatus.NOT_REQUIRED &&
    status !== ClearanceStatus.CLEARANCE_REJECTED;

  const overtimeStatus = isValidStatus(tripData?.overtimeArrivalStatus)
    ? tripData?.overtimeArrivalStatus
    : isValidStatus(tripData?.overtimeDepartureStatus)
      ? tripData?.overtimeDepartureStatus
      : null;

  const escortStatus = isValidStatus(tripData?.escortDepartureStatus)
    ? tripData?.escortDepartureStatus
    : isValidStatus(tripData?.escortArrivalStatus)
      ? tripData?.escortArrivalStatus
      : null;

  const hasBothTerminals =
    tripData?.arrivalAirportTerminal && tripData?.departureAirportTerminal;

  if (error) {
    return (
      <SnackbarAlert type="error">Error loading trip details.</SnackbarAlert>
    );
  }
  const createdBy = tripData?.aircraft?.ownerProfile?.firstName &&
    tripData?.aircraft?.ownerProfile?.lastName
    ? `${tripData?.aircraft?.ownerProfile?.firstName} ${tripData?.aircraft?.ownerProfile?.lastName}` : ""; const contactNumber =
      tripData?.contactNumber || tripData?.aircraft?.ownerProfile?.phone || "---";
  const contactEmail =
    tripData?.aircraft?.ownerProfile?.email || tripData?.creatorEmail || "---";
  return (
    <div className="historic-trips-details-container" data-testid="historic-trips-details">
      <HistoricTripsDetailsHeader currentPage={currentPage} />
      {isLoading && <Loader data-testid="loader" />}
      {!isLoading && (
        <div className="historic-trips-details-row">
          <div className="historic-trips-details-left-column">
            <TripID id={tripData?.trackingId} />
            <PersonalDetails
              createdBy={createdBy}
              contactNumber={contactNumber}
              email={contactEmail}
            />
            <AircraftDetails aircraft={tripData?.aircraft} onMtowResolved={setResolvedMtow} />
            <AirportLocationInformation tripData={tripData} showStatus={true} />
            {(tripData?.requiresDepartureOvertime ||
              tripData?.requiresArrivalOvertime) &&
              (timingArrivalInfo || timingDepartureInfo) && (
                <AfterHoursCard
                  departureTimings={
                    tripData?.requiresDepartureOvertime
                      ? `${timingDepartureInfo.departureAirportOpeningTime} to ${timingDepartureInfo.scheduledTime}`
                      : null
                  }
                  arrivalTimings={
                    tripData?.requiresArrivalOvertime
                      ? `${timingArrivalInfo.requiresArrivalClosingOvertime} to ${timingArrivalInfo.scheduledTime}`
                      : null
                  }
                  departureAirport={airportCodeOnApprove(
                    tripData?.departureAirport.iataCode,
                    tripData?.departureAirport.icaoCode
                  )}
                  arrivalAirport={airportCodeOnApprove(
                    tripData?.arrivalAirport.iataCode,
                    tripData?.arrivalAirport.icaoCode
                  )}
                  onClick={() => handleAfterHoursClick(tripData)}
                />
              )}
            {tripData?.escortVehicles &&
              tripData?.escortVehicles?.length > 0 && (
                <SpecialOnboardScreeningCard
                  onClick={() => handleEscortClick(tripData)}
                />
              )}
          </div>
          <div className="historic-trips-details-left-column">
            {tripData?.passengers && tripData?.passengers?.length > 0 && (
              <PassengerInformation passengers={tripData?.passengers} />
            )}
            {!isExempted && !hasBothTerminals && (
              <PaymentInformation
                tripId={tripId}
                overtimeStatus={overtimeStatus}
                escortStatus={escortStatus}
                hasBothTerminals={hasBothTerminals}
                tripStatus={tripData?.status}
                arrivalTime={tripData?.arrivalTime}
                departureTimeFromArrivalAirport={tripData?.departureTimeFromArrivalAirport}
                mtow={resolvedMtow}
                data-testid="payment-information-data-testid"
              />
            )}
            {tripData?.status !== TRIP_STATUS.CANCELLED && (
              <ApprovalStatus
                isTripCancelled={tripData?.status == TRIP_STATUS.CANCELLED}
                email={isExempted ? tripData?.exemptedByEmail : tripData?.approvedByEmail}
                airport={airportCodeOnApprove(
                  tripData?.arrivalAirport?.iataCode,
                  tripData?.arrivalAirport?.icaoCode
                )}
                isAutoCompleted={tripData?.isAutoCompleted}
                isExempted={isExempted}
                trackingId={tripData?.trackingId}
                timestamp={customDateFormat(isExempted ? tripData?.exemptedAt : tripData?.approvedAt)}
              />
            )}
          </div>
        </div>
      )}
      {!isLoading && tripData?.status != TRIP_STATUS.CANCELLED && (
        <ActionButtons
          arrivalAirport={airportCodeOnApprove(
            tripData?.arrivalAirport?.iataCode,
            tripData?.arrivalAirport?.icaoCode
          )}
          trackingId={tripData?.trackingId}
          tripStatus={tripData?.status}
          userData={userData}
          tripData={tripData}
          data-testid="action-buttons"
        />
      )}
    </div>
  );
};
export default HistoricTripsDetails;
